import { isEmpty, isNil } from "ramda";
import {
  getAlertAdditionalInfo,
  getAnalysisSpatialData,
  getAssetPointsAdditionalData,
  getAssetPointsSerialData,
  getAssetsDates,
  getAssetsDatetimes,
  getChunkInfo,
  getPointsByAssets,
  getReport,
} from "../../api";
import { toast } from "react-toastify";
import { BOOLEAN_MEASURES } from "../../utils/consts";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PhotoIcon from "@mui/icons-material/Photo";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import FolderZipIcon from "@mui/icons-material/FolderZip";

export const loadData = async ({
  asset,
  selectedEquipment,
  dateTimeList,
  points,
  data,
  equipment_ID,
  channel,
  asignedChannels,
  selectedSub,
  assetModule,
  subList,
  measurandsFiltered,
  setData,
  setBooleanData,
  setLoading,
  setPercentiles,
  setDifferences,
}) => {
  let noData = false;
  setLoading(true);
  try {
    const result = [];
    const resultBoolean = [];
    const minPercentiles = {};
    const maxPercentiles = {};
    const differences = {};
    for (let i = 0; i < dateTimeList.length; i++) {
      const date = dateTimeList[i][0];
      const actualChannel = dateTimeList[i][1];
      const actualPoints = points[`${asset.name}${selectedSub.polyline + 1}`];

      const spatialDataResponse = await getAnalysisSpatialData(
        asset.name,
        date,
        equipment_ID,
        actualChannel,
        selectedSub,
        assetModule,
      );
      if (
        !isNil(spatialDataResponse?.status) &&
        spatialDataResponse.status === 200 &&
        !isNil(spatialDataResponse.data)
      ) {
        const spatialData = spatialDataResponse.data.data;
        if (isEmpty(spatialData)) {
          noData = true;
          setLoading(false);
        } else {
          for (let indexMeasurement = 0; indexMeasurement < measurandsFiltered.length; indexMeasurement++) {
            const measurand = measurandsFiltered[indexMeasurement];
            if (!isNil(spatialData.boundaries[measurand]) && !isNil(spatialData.differences[measurand])) {
              if (!isNil(minPercentiles[measurand])) {
                minPercentiles[measurand].push(spatialData.boundaries[measurand][0]);
              } else {
                minPercentiles[measurand] = [spatialData.boundaries[measurand][0]];
              }
              if (!isNil(maxPercentiles[measurand])) {
                maxPercentiles[measurand].push(spatialData.boundaries[measurand][1]);
              } else {
                maxPercentiles[measurand] = [spatialData.boundaries[measurand][1]];
              }
              if (!isNil(differences[measurand])) {
                differences[measurand].push(spatialData.differences[measurand]);
              } else {
                differences[measurand] = [spatialData.differences[measurand]];
              }
            }
          }
          const newItem = {};
          const newItemBoolean = {};
          newItem["date"] = date;
          newItem["pk"] = actualPoints?.points.map((p) => p.pk);
          newItemBoolean["date"] = date;
          newItemBoolean["pk"] = actualPoints?.points.map((p) => p.pk);
          for (let j = 0; j < measurandsFiltered.length; j++) {
            const measureName = measurandsFiltered[j];
            const values = BOOLEAN_MEASURES.includes(measureName)
              ? spatialData.additional_measures?.[measureName]
              : spatialData[measureName];
            if (BOOLEAN_MEASURES.includes(measureName) && !isNil(values)) {
              newItemBoolean[measureName] = {
                datetime: date,
                values: values,
                channel: actualChannel,
              };
            } else if (!isNil(values)) {
              newItem[measureName] = {
                datetime: date,
                values: values,
                channel: actualChannel,
              };
            }
          }
          result.push(newItem);
          resultBoolean.push(newItemBoolean);
        }
      }
    }
    const newPercentiles = {};
    const newDifferences = {};
    for (let indexMeasurement = 0; indexMeasurement < measurandsFiltered.length; indexMeasurement++) {
      const measurand = measurandsFiltered[indexMeasurement];
      if (!isNil(minPercentiles[measurand]) && !isNil(maxPercentiles[measurand]) && !isNil(differences[measurand])) {
        const min = Math.min(...minPercentiles[measurand]);
        const max = Math.max(...maxPercentiles[measurand]);
        const diff = Math.max(...differences[measurand]);
        newPercentiles[measurand] = [min, max];
        newDifferences[measurand] = diff;
      }
    }

    setPercentiles(newPercentiles);
    setDifferences(newDifferences);
    await setData(result);
    await setBooleanData(resultBoolean);
    if (!noData) setLoading(false);
  } catch (error) {
    toast.error(error.message, { className: "toast-error" });
  }
};

export const setEquipmentAndChannelData = async ({
  assetInfo,
  equipmentTypes,
  configData,
  moduleName,
  equipmentID,
  channelParam,
  alertsList,
  newPackage,
  param,
  setMeasurandsOptions,
  setMeasurandsFiltered,
  setSelectedEquipment,
  setEquipment,
  setAsignedChannels,
  setAssetEquipments,
  setChannel,
  setSubList,
  setSelectedSub,
  setEquipmentChannels,
  setFilter,
  setAssetEventList,
  setAssetEvent,
  setAssetAlerts,
  setDateTimeList,
  setAssetSensors,
}) => {
  let auxID = null;
  const newEquipments = [];
  for (let indexEquipment = 0; indexEquipment < assetInfo.equipments.length; indexEquipment++) {
    const actualEquipment = assetInfo.equipments[indexEquipment];
    const moduleFound = configData.modules.find((module) => module.name === moduleName);
    if (!isNil(moduleFound)) {
      if (isNil(auxID)) {
        auxID = actualEquipment.equipmentID;
      }
      newEquipments.push(actualEquipment);
    }
  }
  const equipmentInit = !isNil(equipmentID) ? equipmentID : auxID;
  const equipment = assetInfo.equipments.find((item) => item.equipmentID === equipmentInit);
  const newMeasurands = [];
  for (let indexMeasurand = 0; indexMeasurand < assetInfo.config.modules.length; indexMeasurand++) {
    const actualModule = assetInfo.config.modules[indexMeasurand];
    if (actualModule.name === moduleName) {
      const auxPack = actualModule.packages.filter((pack) => pack.measurand);
      const newPackages = auxPack.map((pack) => pack.name);
      newMeasurands.push(...newPackages);
    }
  }
  const noRepeating = [...new Set(newMeasurands)];
  if (!isEmpty(noRepeating) && !param) {
    setFilter("measurands");
    setAssetEquipments(newEquipments);

    setSelectedEquipment(equipment);
    setEquipment(equipmentInit);
    const menuItems = [];
    for (let index = 0; index < equipment.channels.length; index++) {
      menuItems.push(index + 1);
    }

    const printChannel = equipment.channels.findIndex((channel) => channel.state);
    if (printChannel >= 0) {
      setAsignedChannels(equipment.channels);
      const print = !isNil(channelParam) ? parseInt(channelParam) : printChannel + 1;
      setChannel(print);
      if (print >= 0) {
        setTimeout(() => {
          const newSubList = [];
          for (let indexChannel = 0; indexChannel < equipment.channels.length; indexChannel++) {
            if (equipment.channels[indexChannel].state) {
              newSubList.push(...equipment.channels[indexChannel].substructures);
            }
          }
          setSelectedSub(newSubList[0]);
          setSubList(newSubList);
        }, 0);
      }

      setEquipmentChannels(menuItems);
    } else {
      setAsignedChannels(null);
      setChannel(null);
      setSubList(null);
      setSelectedSub(null);
      setEquipmentChannels(null);
    }
    if (assetInfo.config.additional_sensor.active) {
      setAssetSensors(assetInfo.config.additional_sensor.sensors);
    } else {
      setAssetSensors([]);
    }

    const extraFilters = [];
    for (
      let indexMeasurand = 0;
      indexMeasurand < assetInfo.config.additional_sensor.measurands.length;
      indexMeasurand++
    ) {
      const category = assetInfo.config.additional_sensor.measurands[indexMeasurand].category;
      if (!extraFilters.includes(category)) {
        extraFilters.push(category);
      }
    }
    const finalFilters = [...noRepeating, ...extraFilters];
    setMeasurandsOptions(finalFilters);
    setMeasurandsFiltered(noRepeating);
  } else {
    const newAlertList = alertsList.measurements.filter(
      (alert) => alert.asset === assetInfo.name && alert.module === moduleName,
    );
    const auxEvents = newAlertList.map((alert) => alert.package);
    const newEvents = auxEvents.filter((item, index) => auxEvents.indexOf(item) === index);
    setAssetEventList(newEvents);
    setAssetEvent(!isNil(newPackage) ? newPackage : newEvents[0]);
    setAssetAlerts(newAlertList);
    setAsignedChannels(null);
    setChannel(null);
    setSubList(null);
    setSelectedSub(null);
    setDateTimeList([]);
    setEquipmentChannels(null);
    setFilter("alerts");
  }
};

export const loadDateOptionsChannel = async ({
  date,
  time,
  asset,
  equipment,
  channel,
  selectedSub,
  dateTime,
  equipmentChannels,
  assetEquipments,
  dateOptions,
  setDateOptions = () => {},
  setTimeOptions = () => {},
  setDateOptionsModal = () => {},
  setTimeOptionsModal = () => {},
  setDate = () => {},
  setTime = () => {},
  setLoading = () => {},
  setDateTimeList = () => {},
  setChannel,
  setSelectedSub,
  firstTime,
  setFirstTime,
  selectedEquipment,
  setSelectedEquipment,
}) => {
  if (!isNil(asset) && !isNil(equipment) && !isNil(channel)) {
    let dates = null;
    let changedEquipment = equipment;
    let changedChannel = channel;
    let changedSub = selectedSub;
    let found = false;
    if (isNil(date)) {
      for (let indexEquipment = 0; indexEquipment < assetEquipments.length && !found; indexEquipment++) {
        if (selectedEquipment === assetEquipments[indexEquipment] || firstTime) {
          for (
            let indexChannel = 0;
            indexChannel < assetEquipments[indexEquipment].channels.length && !found;
            indexChannel++
          ) {
            if (
              (channel === indexChannel + 1 || firstTime) &&
              assetEquipments[indexEquipment].channels[indexChannel].state
            ) {
              for (
                let indexSub = 0;
                indexSub < assetEquipments[indexEquipment].channels[indexChannel].substructures.length;
                indexSub++
              ) {
                const actualSub = assetEquipments[indexEquipment].channels[indexChannel].substructures[indexSub];
                const response = await getAssetsDates(
                  [asset],
                  selectedEquipment.equipmentID,
                  indexChannel + 1,
                  actualSub.name,
                );
                dates = response.data.data;

                if (!isEmpty(dates)) {
                  setFirstTime(false);
                  if (indexChannel + 1 !== channel) {
                    setChannel(indexChannel + 1);
                  }
                  setSelectedEquipment(assetEquipments[indexEquipment]);
                  changedEquipment = assetEquipments[indexEquipment].equipmentID;
                  changedChannel = indexChannel + 1;
                  changedSub = actualSub;

                  found = true;
                  break;
                }
              }
            }
          }
        }
      }
    }
    let timeResponse = null;
    if ((!isEmpty(dates) && !isNil(dates)) || !isNil(date)) {
      timeResponse = await getAssetsDatetimes(
        [asset],
        changedEquipment,
        changedChannel,
        changedSub.name,
        !isNil(date) ? date : dates[0],
      );
    }
    if (
      !isNil(timeResponse?.status) &&
      timeResponse.status === 200 &&
      !isNil(timeResponse.data) &&
      !isEmpty(timeResponse.data.data)
    ) {
      const times = timeResponse.data.data;

      let localDateIndex = isNil(dates) ? dateOptions.indexOf(date) : 0;

      let localTimeIndex = isNil(time) ? 0 : times.indexOf(time);

      setDate(localDateIndex);
      setTime(localTimeIndex);
      setDateOptions(dates);
      setTimeOptions(times);
      if (!isNil(dates)) {
        setDateOptionsModal(dates);
      }
      setTimeOptionsModal(times);
      setLoading(false);
    } else {
      setDateOptions(null);
      setTimeOptions(null);
      setDateOptionsModal(null);
      setTimeOptionsModal(null);
      setDate(null);
      setTime(null);
      setDateTimeList([]);
      setLoading(true);
    }
  } else {
    setDateOptions(null);
    setTimeOptions(null);
    setDateOptionsModal(null);
    setTimeOptionsModal(null);
    setDate(null);
    setTime(null);
    setDateTimeList([]);
    setLoading(true);
  }
};

export const handleModalAccept = ({ date, time, modalChannel, substructure, dateTimeList, setDateTimeList }) => {
  const dateStr = `${date}T${time}`;
  if (!dateTimeList.includes(dateStr)) {
    setDateTimeList([...dateTimeList, [dateStr, modalChannel, substructure.polyline]]);
  }
};

export const initialSpatialSelectHandler = ({
  data,
  start,
  end,
  measureName,
  setTemporalDataList,
  minsWaterfall,
  setMinsWaterfall,
  maxsWaterfall,
  setMaxsWaterfall,
  currentAsset,
  spatialDataList,
  setLoading,
  dateTime,
  equipment,
  selectedEquipment,
  channel,
}) => {
  setLoading(true);
  let i = start;
  let j = end;
  if (j - i >= 64) {
    let centerPoint = parseInt((j - i) / 2) + i;
    i = centerPoint >= 32 ? centerPoint - 32 : 0;
    centerPoint += 32;
    const count = data?.series?.[0]?.data?.length ?? 0;
    j = centerPoint < count ? centerPoint : count - 1;
  }
  //initialLoadTemporalData({
  //  asset: currentAsset,
  //  startIndex: i,
  //  endIndex: j,
  //  pkList: spatialDataList?.[0]?.pk?.slice(i, j + 1),
  //  measureName,
  //  setTemporalDataList,
  //  setMinsWaterfall,
  //  setMaxsWaterfall,
  //  minsWaterfall,
  //  maxsWaterfall,
  //  setLoading,
  //  dateTime,
  //  selectedEquipment,
  //  equipment,
  //  channel,
  //});
  //setLoading(false);
};

export const initialLoadTemporalData = async ({
  asset,
  startIndex,
  endIndex,
  pkList,
  measureName,
  setTemporalDataList,
  setMinsWaterfall,
  setMaxsWaterfall,
  setLoading,
  dateTime,
  selectedEquipment,
  equipment,
  channel,
}) => {
  try {
    const result = {};
    const minsWaterfall = {};
    const maxsWaterfall = {};

    const measures = selectedEquipment.pMeasureNames.filter((m) => !BOOLEAN_MEASURES.includes(m));
    for (let mI = 0; mI < measures.length; mI++) {
      const measureName = measures[mI];
      const response = await getAssetPointsSerialData(
        asset.name,
        startIndex,
        endIndex,
        dateTime,
        [measureName],
        equipment,
        channel,
        30,
        5000,
      );
      if (!isNil(response?.status) && response.status === 200 && !isNil(response.data)) {
        const data = response.data?.data;

        let minValue = Infinity;
        let maxValue = Number.NEGATIVE_INFINITY;
        if (isNil(result[measureName])) {
          result[measureName] = [];
        }
        for (let i = 0, j = startIndex; i < data.data.length; i++, j++) {
          const item = data.data[i];
          const values = item[measureName].map((item) => item.value);
          const min = Math.min(...values);
          const max = Math.max(...values);
          if (minValue > min) minValue = min;
          if (maxValue < max) maxValue = max;

          result[measureName].push({
            values: item[measureName].map((v) => v.value),
            datetimes: item[measureName].map((v) => v.datetime),
            pk: pkList[i],
          });
        }
        if (Math.abs(minValue) > maxValue && measureName === "strain") {
          maxValue = Math.abs(minValue);
        }
        minsWaterfall[measureName] = minValue;
        maxsWaterfall[measureName] = maxValue;
      }
    }

    setMinsWaterfall(minsWaterfall);
    setMaxsWaterfall(maxsWaterfall);
    setTemporalDataList(result);
  } catch (error) {
    toast.error(error.message, { className: "toast-error" });
  }
  setLoading(false);
};

export const IconDisplay = ({ filename, fileIndex }) => {
  const extension = obtainExtension(filename);
  const type = fileType(extension);

  if (type === "image") {
    return <PhotoIcon id={`display_${fileIndex}_${type}`} sx={{ cursor: "pointer", margin: "1%" }} />;
  }
  if (type === "video") {
    return <OndemandVideoIcon id={`display_${fileIndex}_${type}`} sx={{ cursor: "pointer", margin: "1%" }} />;
  }
  if (type === "audio") {
    return <MusicNoteIcon id={`display_${fileIndex}_${type}`} sx={{ cursor: "pointer", margin: "1%" }} />;
  }
  if (type === "compressed") {
    return <FolderZipIcon id={`display_${fileIndex}_${type}`} sx={{ cursor: "pointer", margin: "1%" }} />;
  }
  if (filename.endsWith(".pdf")) {
    return <PictureAsPdfIcon id={`display_${fileIndex}_${type}`} sx={{ cursor: "pointer", margin: "1%" }} />;
  } else {
    return <InsertDriveFileIcon id={`display_${fileIndex}_${type}`} sx={{ cursor: "pointer", margin: "1%" }} />;
  }
};
function obtainExtension(file) {
  const parts = file.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
}

function fileType(extension) {
  for (const type in fileTypes) {
    if (fileTypes[type].includes(extension)) {
      return type;
    }
  }
  return null;
}
const fileTypes = {
  image: ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp", "tiff"],
  video: ["mp4", "avi", "mkv", "mov", "wmv", "flv", "webm", "mpeg"],
  sound: ["mp3", "wav", "ogg", "flac", "aac", "m4a", "wma"],
  compressed: ["zip", "rar", "7z", "tar", "gz", "bz2"],
};

export const reportFiling = async ({ id, setReport, setReportImages, setReportFiles, setLoading }) => {
  const reportResult = await getReport(id); //reportID
  const images = [];
  const otherFiles = [];
  //aqui hay que ordenar los archivos por imagenes primero y luego lo demás
  for (let indexFile = 0; indexFile < reportResult.data.data.attachments.length; indexFile++) {
    const extension = obtainExtension(reportResult.data.data.attachments[indexFile].filename);
    const imageChecker = fileType(extension, ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp", "tiff"]);
    if (imageChecker === "image") {
      images.push(reportResult.data.data.attachments[indexFile]);
    } else {
      otherFiles.push(reportResult.data.data.attachments[indexFile]);
    }
  }
  setReport(reportResult.data.data);
  setReportImages(images);
  setReportFiles(otherFiles);
  setLoading(false);
};

export const getPointsMap = async ({ assetsPoints, asset, dispatch, setAssetsPointsData }) => {
  const alreadyPoints = assetsPoints.data;
  if (!isNil(alreadyPoints)) {
    const puntosExistentes = (alreadyPoints, asset) => {
      return Object.keys(alreadyPoints).some((key) => key.startsWith(asset));
    };
    if (!puntosExistentes(alreadyPoints, asset) && !isNil(asset)) {
      const response = await getPointsByAssets(asset);
      const pointsMap = {};
      for (let index = 0; index < response.data.data.length; index++) {
        pointsMap[`${asset}${index + 1}`] = response.data.data[index];
      }
      dispatch(setAssetsPointsData(pointsMap));
    }
  }
};

export const manageAlertsAndReports = async ({
  assetAlerts,
  modulesEvents,
  setSelectedAlert,
  assetModule,
  actualEvent,
  dateTime,
  setAlertTimes,
  setAssetEvent,
  setEventAlerts,
  setReport,
  setReportImages,
  setReportFiles,
  setDate,
  setTime,
  setDateOptions,
  setTimeOptions,
  setDateOptionsModal,
  setTimeOptionsModal,
  setAlertAdditionalInfo,
  setSelectedAdditionalInfo,
  setChunk,
  setLoading,
}) => {
  setLoading(true);
  const dates = [];
  const times = [];
  const newAlerts = assetAlerts.filter((aa) => aa.package === actualEvent);
  for (let aaIndex = 0; aaIndex < newAlerts.length; aaIndex++) {
    const temp =
      newAlerts[aaIndex].dateStart.indexOf("T") !== -1
        ? newAlerts[aaIndex].dateStart.split("T")
        : newAlerts[aaIndex].dateStart.split(" ");
    if (dates.includes(temp[0])) {
      const indexDate = dates.indexOf(temp[0]);
      times[indexDate].push(temp[1]);
    } else {
      dates.push(temp[0]);
      times.push([temp[1]]);
    }
  }
  if (!isEmpty(dates)) {
    const actualAlert = !isNil(dateTime) ? newAlerts.find((aa) => aa.dateStart === dateTime) : newAlerts[0];
    if (!isNil(actualAlert)) {
      const temp =
        actualAlert.dateStart.indexOf("T") !== -1 ? actualAlert.dateStart.split("T") : actualAlert.dateStart.split(" ");
      setDate(dates.indexOf(temp[0]));
      setTime(times[dates.indexOf(temp[0])].indexOf(temp[1]));
      setDateOptions(dates);
      setAlertTimes(times);
      setTimeOptions(times[dates.indexOf(temp[0])]);
      if (!isNil(dates)) {
        setDateOptionsModal(dates);
      }
      setTimeOptionsModal(times[dates.indexOf(temp[0])]);
    }
    if (!isEmpty(dates) && !isEmpty(times)) {
      setSelectedAlert(actualAlert);
      const newAdditionalInfo = await getAlertAdditionalInfo(actualAlert.id);
      setAlertAdditionalInfo(newAdditionalInfo.data.data); // esto se setea para conseguir los chunks
      if (!isEmpty(newAdditionalInfo.data.data)) {
        setSelectedAdditionalInfo(newAdditionalInfo.data.data[0].id);
        const newChunk = await getChunkInfo(newAdditionalInfo.data.data[0].id);
        setChunk(newChunk.data.data);
      } else {
        setSelectedAdditionalInfo(null);
        setChunk([]);
      }

      let reportIndex = null;
      if (!isNil(actualAlert.reports)) {
        for (let alertRepIndex = 0; alertRepIndex < actualAlert.reports.length; alertRepIndex++) {
          if (!isNil(actualAlert.reports[alertRepIndex]) && !isEmpty(actualAlert.reports[alertRepIndex])) {
            reportIndex = alertRepIndex;
            break;
          }
        }
      }
      setEventAlerts(newAlerts);
      if (!isNil(reportIndex)) {
        reportFiling({
          id: actualAlert.reports[reportIndex].id,
          setReport,
          setReportImages,
          setReportFiles,
          setLoading,
        });
      } else {
        setLoading(false);
      }
    }
  } else {
    setDate([]);
    setTime([]);
    setDateOptions([]);
    setAlertTimes([]);
    setTimeOptions([]);
    setDateOptionsModal([]);
    setTimeOptionsModal([]);
    setReport(null);
    setReportFiles(null);
    setLoading(false);
  }
};

export const getCurrentIntensityData = async ({
  data,
  setIntesityData,
  asset,
  assetInfo,
  assetSensors,
  sensorCats,
  catRefs,
  equipment,
  selectedEquipment,
  channel,
  startDate,
  afterAlert,
  dateTimeList,
  startIndex,
  endIndex,
  period = 30,
  maximum = 1000,
  setLoadingIntensity,
}) => {
  setLoadingIntensity(true);
  const allSensorIDs = [];
  const allSensorIDsMeasurands = [];
  const response = [];
  for (let indexSensor = 0; indexSensor < assetSensors.length; indexSensor++) {
    for (let indexMeasurand = 0; indexMeasurand < assetSensors[indexSensor].measurands.length; indexMeasurand++) {
      let check = false;
      for (let indexCat = 0; indexCat < sensorCats.length; indexCat++) {
        if (catRefs[sensorCats[indexCat]].includes(assetSensors[indexSensor].measurands[indexMeasurand])) check = true;
        if (check) break;
      }
      if (check) {
        allSensorIDs.push(assetSensors[indexSensor].sensor_ID);
        allSensorIDsMeasurands.push([assetSensors[indexSensor].measurands[indexMeasurand]]);
        const addition = await getAssetPointsAdditionalData(
          asset,
          dateTimeList[0][0],
          assetSensors[indexSensor].measurands[indexMeasurand], //medida
          equipment,
          period,
          maximum,
          channel,
          startDate,
          afterAlert,
          assetSensors[indexSensor].sensor_ID, //ID
          startIndex,
          endIndex,
        );
        response.push(addition);
      }
    }
  }
  const result = [];
  let minValue = Infinity;
  let maxValue = Number.NEGATIVE_INFINITY;
  const pkList = data?.[0].pk;
  for (let index = 0; index < response.length; index++) {
    if (!isNil(response[index]?.status) && response[index].status === 200 && !isNil(response[index].data)) {
      const data = response[index].data?.data;
      if (!isNil(data.data)) {
        for (let i = 0, j = startIndex; i < data.data.length; i++, j++) {
          for (let indexMeasurand = 0; indexMeasurand < allSensorIDsMeasurands[index].length; indexMeasurand++) {
            const item = data.data[i];
            if (!isNil(item[allSensorIDsMeasurands[index][indexMeasurand]])) {
              const values = item[allSensorIDsMeasurands[index][indexMeasurand]].map((item) => item.value);
              const min = Math.min(...values);
              const max = Math.max(...values);
              if (minValue > min) minValue = min;
              if (maxValue < max) maxValue = max;
              result.push({
                sensor_ID: allSensorIDs[index],
                values: item[allSensorIDsMeasurands[index][indexMeasurand]].map((v) => v.value),
                datetimes: item[allSensorIDsMeasurands[index][indexMeasurand]].map((v) => v.datetime),
                pk: pkList[i],
                measurand: allSensorIDsMeasurands[index][indexMeasurand],
              });
              if (Math.abs(minValue) > maxValue && allSensorIDsMeasurands[index][indexMeasurand] === "strain") {
                maxValue = Math.abs(minValue);
              }
            }
          }
        }
      }
    }
  }
  setIntesityData(result);
  setLoadingIntensity(false);
};
